import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import { FinancialControllerService } from 'src/app/admin-api/api/financialController.service';
import { SubscriptionTypeControllerService } from 'src/app/admin-api/api/subscriptionTypeController.service';
import { Gateway } from 'src/app/admin-api/model/gateway';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface PlanGatewayRow {
  subscriptionTypeId: number;
  label: string;
  gatewayIds: Array<number | null>;
}

@Component({
  selector: 'app-billing-control-gateway-priority-modal',
  templateUrl: './billing-control-gateway-priority-modal.component.html',
  styleUrls: ['./billing-control-gateway-priority-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BillingControlGatewayPriorityModalComponent implements OnInit {
  private financialService = inject(FinancialControllerService);
  private subscriptionTypeService = inject(SubscriptionTypeControllerService);
  private messageService = inject(MessageService);
  private dialogRef = inject(DynamicDialogRef);

  gatewayList: Gateway[] = [];
  rows: PlanGatewayRow[] = [];
  priorityColumns: number[] = [0];
  bulkPrimaryGatewayId: number | null = null;
  isSaving = false;

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      await this.loadTable();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  getGatewayOptions(row: PlanGatewayRow, columnIndex: number): Gateway[] {
    const selectedInOtherColumns = new Set(
      row.gatewayIds
        .filter((_, index) => index !== columnIndex)
        .filter((gatewayId): gatewayId is number => gatewayId != null)
    );

    return this.gatewayList.filter(
      (gateway) =>
        gateway.gatewayId != null &&
        (!selectedInOtherColumns.has(gateway.gatewayId) ||
          gateway.gatewayId === row.gatewayIds[columnIndex])
    );
  }

  addPriorityColumn(): void {
    if (this.priorityColumns.length >= this.gatewayList.length) {
      return;
    }

    this.priorityColumns = [...this.priorityColumns, this.priorityColumns.length];
    this.rows = this.rows.map((row) => ({
      ...row,
      gatewayIds: [...row.gatewayIds, null]
    }));
  }

  canAddPriorityColumn(): boolean {
    return this.priorityColumns.length < this.gatewayList.length;
  }

  applyPrimaryGatewayToAll(): void {
    if (this.bulkPrimaryGatewayId == null || this.rows.length === 0) {
      return;
    }

    if (this.priorityColumns.length === 0) {
      this.priorityColumns = [0];
    }

    const primaryGatewayId = this.bulkPrimaryGatewayId;
    this.rows = this.rows.map((row) => {
      const gatewayIds = this.padGatewayIds(
        [...row.gatewayIds],
        this.priorityColumns.length
      );

      for (let index = 1; index < gatewayIds.length; index++) {
        if (gatewayIds[index] === primaryGatewayId) {
          gatewayIds[index] = null;
        }
      }
      gatewayIds[0] = primaryGatewayId;

      return {
        ...row,
        gatewayIds
      };
    });

    this.messageService.add({
      severity: 'info',
      summary: 'Aplicado',
      detail:
        'Gateway definido como prioridade 1 em todos os planos. Clique em Salvar para confirmar.'
    });
  }

  async saveGatewayPriority(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    LoaderService.showLoader();
    try {
      // Save one plan at a time to avoid SQL Server deadlocks on parallel deletes.
      for (const row of this.rows) {
        const gatewayIds = row.gatewayIds.filter(
          (gatewayId): gatewayId is number => gatewayId != null
        );
        await lastValueFrom(
          this.subscriptionTypeService.updateGatewayPriorityBySubscriptionTypeId(
            gatewayIds,
            row.subscriptionTypeId
          )
        );
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Salvo',
        detail: 'Prioridades de gateway atualizadas para todos os planos.'
      });
      this.dialogRef.close(true);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.isSaving = false;
      LoaderService.showLoader(false);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private async loadTable(): Promise<void> {
    const [subscriptionGroups, gateways] = await Promise.all([
      lastValueFrom(
        this.subscriptionTypeService
          .findAllSubscriptionByTypes()
          .pipe(map((response) => response.result ?? []))
      ),
      lastValueFrom(
        this.financialService.findGatewaysList().pipe(map((response) => response.result ?? []))
      )
    ]);

    this.gatewayList = gateways.filter((gateway) => gateway.gatewayId != null);

    const plans: Array<{ subscriptionTypeId: number; label: string }> = [];
    for (const group of subscriptionGroups) {
      for (const type of group.subscriptionTypes ?? []) {
        if (type.subscriptionTypeId == null) {
          continue;
        }

        const subscriptionName = this.normalizeLabel(
          group.subscriptionName ?? group.subscriptionId
        );
        const typeName = this.normalizeLabel(
          type.subscriptionTypeName ?? type.subscriptionTypeId
        );

        plans.push({
          subscriptionTypeId: type.subscriptionTypeId,
          label: `${subscriptionName} — ${typeName}`
        });
      }
    }

    plans.sort((left, right) => left.label.localeCompare(right.label));

    const prioritiesByPlan = await Promise.all(
      plans.map(async (plan) => {
        const priorities = await lastValueFrom(
          this.subscriptionTypeService
            .getGatewayPriorityBySubscriptionTypeId(plan.subscriptionTypeId)
            .pipe(map((response) => response.result ?? []))
        );

        const gatewayIds = priorities
          .sort((left, right) => (left.gatewayOrder ?? 0) - (right.gatewayOrder ?? 0))
          .map((priority) => priority.gatewayId ?? null);

        return {
          subscriptionTypeId: plan.subscriptionTypeId,
          label: plan.label,
          gatewayIds
        } as PlanGatewayRow;
      })
    );

    const maxPriority = Math.max(
      1,
      ...prioritiesByPlan.map((row) => row.gatewayIds.length),
      Math.min(2, this.gatewayList.length || 1)
    );

    this.priorityColumns = Array.from({ length: maxPriority }, (_, index) => index);
    this.rows = prioritiesByPlan.map((row) => ({
      ...row,
      gatewayIds: this.padGatewayIds(row.gatewayIds, maxPriority)
    }));
  }

  private padGatewayIds(
    gatewayIds: Array<number | null>,
    size: number
  ): Array<number | null> {
    const padded = [...gatewayIds];
    while (padded.length < size) {
      padded.push(null);
    }
    return padded.slice(0, size);
  }

  private normalizeLabel(value: unknown): string {
    if (value == null) {
      return '';
    }
    return String(value).replace('Anual.', 'Anual/mês');
  }
}
